import { useState, useEffect, useCallback } from "react";
import { GitPullRequest, RefreshCw } from "lucide-react";
import { useProject } from "../contexts/ProjectContext";
import { authFetch } from "../utils/authFetch";
import { useMergeRequests } from "../hooks/useMergeRequests";
import type { MergeRequest } from "../hooks/useMergeRequests";
import MergeRequestList from "../components/docs/MergeRequestList";
import MergePreviewDialog from "../components/docs/MergePreviewDialog";

/**
 * Merge Request Dashboard — list of MRs, click opens merge preview dialog.
 */
export default function MergeRequestDashboardPage() {
  const { currentProject } = useProject();
  const projectSlug = currentProject?.slug;

  const [statusFilter, setStatusFilter] = useState("");
  const [targetBranchFilter, setTargetBranchFilter] = useState("");
  const [activeMr, setActiveMr] = useState<MergeRequest | null>(null);
  // Full branch records (not just id/name) so the "New request" CTA can
  // pre-fill the dialog with a sensible default source (most recent
  // non-protected draft) and target (main).
  type BranchRow = { id: string; name: string; isProtected?: boolean; updatedAt?: string };
  const [branchRows, setBranchRows] = useState<BranchRow[]>([]);
  const branches = branchRows.map((b) => ({ id: b.id, name: b.name }));
  // State for the "New request" flow — same MergePreviewDialog, opened
  // from the list page with pre-filled source/target instead of from a
  // specific MR row.
  const [newRequest, setNewRequest] = useState<{
    sourceId: string; sourceName: string;
    targetId: string; targetName: string;
  } | null>(null);

  useEffect(() => {
    if (!projectSlug) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`/api/projects/${projectSlug}/studio/branches`);
        if (cancelled || !res.ok) return;
        const data = await res.json();
        setBranchRows(data.map((b: any) => ({
          id: b.id, name: b.name,
          isProtected: !!b.isProtected,
          updatedAt: b.updatedAt,
        })));
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [projectSlug]);

  const handleNewRequest = useCallback(() => {
    // Defaults: source = most recently updated non-protected draft,
    // target = main. Both can be edited inside the dialog. If neither
    // a draft nor main is available we no-op — there's nothing useful
    // we could prefill.
    const main = branchRows.find((b) => b.name === "main");
    const draft = [...branchRows]
      .filter((b) => !b.isProtected && b.name !== "main")
      .sort((a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || ""))[0];
    if (!main || !draft) return;
    setNewRequest({
      sourceId: draft.id, sourceName: draft.name,
      targetId: main.id, targetName: main.name,
    });
  }, [branchRows]);

  const filters = {
    status: statusFilter || undefined,
    targetBranch: targetBranchFilter || undefined,
  };
  const { mergeRequests, loading, refetch } = useMergeRequests(filters);

  const handleSelect = useCallback((mr: MergeRequest) => {
    setActiveMr(mr);
  }, []);

  const handleDialogClose = useCallback(() => {
    setActiveMr(null);
  }, []);

  const handleMerged = useCallback(() => {
    setActiveMr(null);
    refetch();
  }, [refetch]);

  return (
    <div className="space-y-4 px-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GitPullRequest className="size-5 text-indigo-500" />
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Merge Requests</h2>
            <p className="text-[13px] text-slate-500 mt-0.5">
              Review and manage merge requests
            </p>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          title="Refresh"
        >
          <RefreshCw className="size-4" />
        </button>
      </div>

      {/* List */}
      <MergeRequestList
        mergeRequests={mergeRequests}
        loading={loading}
        onSelect={handleSelect}
        selectedId={activeMr?.id}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        targetBranchFilter={targetBranchFilter}
        onTargetBranchFilterChange={setTargetBranchFilter}
        branches={branches}
        onNewRequest={handleNewRequest}
      />

      {/* Merge preview dialog on click */}
      {activeMr && (
        <MergePreviewDialog
          branchId={activeMr.sourceBranchId}
          branchName={activeMr.sourceBranchName ?? activeMr.sourceBranchId.slice(0, 8)}
          onClose={handleDialogClose}
          onMerged={handleMerged}
          mergeRequestId={activeMr.id}
          mergeRequestStatus={activeMr.status}
          mergeRequestStatusReason={activeMr.statusReason}
          initialStrategy={activeMr.strategy}
          sourceHeadAtCreation={activeMr.sourceHeadAtCreation}
          targetHeadAtCreation={activeMr.targetHeadAtCreation}
          sourceSequence={activeMr.sourceSequence}
          targetSequence={activeMr.targetSequence}
          targetBranchId={activeMr.targetBranchId}
          targetBranchName={activeMr.targetBranchName}
          // The requester can withdraw (Close) her own request.
          mergeRequestAuthor={activeMr.requestedBy}
          mergeHeadCommitId={activeMr.mergeHeadCommitId}
        />
      )}

      {/* New-request dialog — same component, opened from the page
          header rather than a row. No mergeRequestId means the dialog
          renders its "create" path instead of the existing-MR review
          path. */}
      {newRequest && (
        <MergePreviewDialog
          branchId={newRequest.sourceId}
          branchName={newRequest.sourceName}
          targetBranchId={newRequest.targetId}
          targetBranchName={newRequest.targetName}
          initialStrategy="merge"
          onClose={() => setNewRequest(null)}
          onMerged={() => { setNewRequest(null); refetch(); }}
        />
      )}
    </div>
  );
}
